// Modali della lista Clienti (design_handoff_modali_clienti, opzione 10a):
// Girati, Call obiettivo, Girato di recupero, Task, Follower, Servizi e
// importi, Dati amministrativi. Qui c'è solo la parte visiva: stato, handler e
// chiamate API restano in pages/ClientsPage.jsx, che passa tutto come prop.
// Così il comportamento è quello di prima e cambia solo il modo di presentarlo.
// Fa eccezione ModaleGirati, che carica e salva da sola (vedi il suo commento).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Phone,
  RotateCcw,
  ListChecks,
  TrendingUp,
  Package,
  FileText,
  X,
  Eye,
  RefreshCw,
  Loader2,
  Link2,
  ExternalLink,
  CreditCard,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  SHADOWS,
  ICON_TILES,
  SECONDARY_BTN,
  ToggleChip,
  DatePicker,
  MonthPicker,
  UserAvatar,
  InfoTooltip,
} from "./designSystem";
import { apiFetch } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";

// ==================== Cornice comune ====================

// Colore di ogni modale: tile dell'icona, bottone primario, focus dei campi.
const TONI = {
  accento: { tile: ICON_TILES.viola, colore: "#5b5bd6", hover: "#4a4ac4", anello: "rgba(91,91,214,.14)", ombra: "rgba(91,91,214,.9)" },
  ambra: { tile: { bg: "#fef3c7", color: "#d97706" }, colore: "#d97706", hover: "#b45309", anello: "rgba(217,119,6,.16)", ombra: "rgba(217,119,6,.8)" },
  lilla: { tile: { bg: "#f5ecfc", color: "#9b3fd6" }, colore: "#9b3fd6", hover: "#8330bd", anello: "rgba(155,63,214,.14)", ombra: "rgba(155,63,214,.8)" },
};

// Campo di testo delle modali: 42px, ombra inset a riposo, al focus bordo e
// anello del colore della modale (variabili --mc / --mc-anello impostate dalla
// cornice) e sollevamento di 1px.
const CAMPO =
  "w-full h-[42px] rounded-[10px] border border-[#e4e4ed] bg-white px-3 text-[14px] text-[#15161c] outline-none " +
  "shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] hover:border-[#c9c9f0] " +
  "focus:border-[var(--mc)] focus:shadow-[0_0_0_3px_var(--mc-anello),0_8px_18px_-10px_var(--mc)] focus:-translate-y-px " +
  "transition-[box-shadow,border-color,transform] duration-200 scheda-font-ui";

const ETICHETTA = "text-[12.5px] font-semibold text-[#3c414d]";

// Interruttore 40×24 con il colore passato (teal, ambra, verde…), stesso
// disegno di ToggleSwitch del design system.
function Interruttore({ checked, colore = "#5b5bd6", testId, label }) {
  return (
    <span
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      className="relative w-10 h-6 rounded-full shrink-0 transition-colors duration-200 shadow-[inset_0_1px_2px_rgba(15,23,42,.12)]"
      style={{ background: checked ? colore : "#dcdce8" }}
      data-testid={testId}
      data-state={checked ? "checked" : "unchecked"}
    >
      <span
        className="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-[220ms] ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none"
        style={{ transform: `translateX(${checked ? 16 : 0}px)`, boxShadow: "0 1px 3px rgba(15,23,42,.25)" }}
      />
    </span>
  );
}

function ModaleCliente({
  open, onClose, tono = "accento", icon: Icon, titolo, sottotitolo, headerExtra,
  children, stato, piedeSinistra, primario, testId,
}) {
  const t = TONI[tono];
  const abilitato = !primario.disabled && !primario.saving;
  const invia = (e) => {
    e.preventDefault();
    if (abilitato) primario.onClick();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[520px] w-[calc(100vw-32px)] p-0 gap-0 rounded-[20px] sm:rounded-[20px] border-[#e9e9f1] bg-white scheda-font-ui [&>button.absolute]:hidden"
        style={{ boxShadow: SHADOWS.flottante, "--mc": t.colore, "--mc-anello": t.anello }}
        onOpenAutoFocus={(e) => {
          // Focus sul primo campo invece che sul bottone di chiusura.
          const primo = e.currentTarget?.querySelector?.("[data-autofocus]");
          if (primo) {
            e.preventDefault();
            primo.focus();
          }
        }}
        data-testid={testId}
      >
        <form onSubmit={invia} className="flex flex-col min-w-0">
          <div className="flex items-start gap-3 pt-[22px] pr-[22px] pl-6">
            <div
              className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0"
              style={{ background: t.tile.bg, boxShadow: SHADOWS.appoggiato }}
            >
              <Icon className="w-5 h-5" style={{ color: t.tile.color }} strokeWidth={1.9} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-0.5 pt-0.5">
              <DialogTitle className="text-[17px] font-bold text-[#15161c] leading-tight">{titolo}</DialogTitle>
              <DialogDescription className="text-[12.5px] text-[#565d6d] truncate">{sottotitolo}</DialogDescription>
            </div>
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="w-8 h-8 rounded-[9px] flex items-center justify-center text-[#9095a1] hover:bg-[#f3f3f8] hover:text-[#15161c] transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-6 py-5 flex flex-col gap-4 min-w-0">{children}</div>

          <div className="border-t border-[#f0f0f6] bg-[#fbfbfd] rounded-b-[20px] px-6 py-3.5 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              {piedeSinistra || (stato && <span className="text-[12px] text-[#9095a1]">{stato}</span>)}
            </div>
            <button type="button" onClick={onClose} className={`${SECONDARY_BTN} h-10 px-4 text-[13.5px]`}>
              Annulla
            </button>
            <button
              type="submit"
              disabled={!abilitato}
              className="h-10 px-4 rounded-[10px] text-[13.5px] font-semibold inline-flex items-center gap-1.5 transition-colors shrink-0"
              style={abilitato
                ? { background: t.colore, color: "#fff", boxShadow: `0 8px 20px -8px ${t.ombra}` }
                : { background: "#EFF0F7", color: "#9095a1", cursor: primario.saving ? "wait" : "not-allowed" }}
              onMouseEnter={(e) => { if (abilitato) e.currentTarget.style.background = t.hover; }}
              onMouseLeave={(e) => { if (abilitato) e.currentTarget.style.background = t.colore; }}
              data-testid={primario.testId}
            >
              {primario.saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {primario.saving ? "Salvataggio…" : primario.label}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const soloData = (iso) => (iso ? String(iso).split("T")[0] : "");
const oggiPiu = (giorni) => {
  const d = new Date();
  d.setDate(d.getDate() + giorni);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ==================== 1. Girati ====================
// Più date di girato per cliente (backend/routes/girati.py). Si fissa solo il
// giorno: le task generate arrivano già ai calendari del team in base ai
// servizi del cliente, scegliere qui una persona era un passaggio inutile.
// I girati vecchi che hanno una persona la mostrano ancora. Eccezione alla
// regola del file: la modale carica e salva da sola, perché calendario e
// agenda di tutti i clienti cambiano a ogni azione e passarli come prop dalla
// pagina non aggiunge nulla. Dopo ogni salvataggio avvisa la pagina con `onCambiato` (data_girato del
// cliente e colonna Date possono essere cambiati).

const MESI_LUNGHI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const GIORNI_BREVI = ["L", "M", "M", "G", "V", "S", "D"];
const GIORNI_LUNGHI = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
const isoLocale = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daIso = (iso) => new Date(`${iso}T00:00:00`);
const giornoEsteso = (iso) => {
  const d = daIso(iso);
  return `${GIORNI_LUNGHI[d.getDay()]} ${d.getDate()} ${MESI_LUNGHI[d.getMonth()]}`;
};
const dataCorta = (iso) => {
  const d = daIso(iso);
  return `${d.getDate()} ${MESI_LUNGHI[d.getMonth()].slice(0, 3)}`;
};

// Contenuto del tooltip di un giorno del calendario (sfondo accento, testo bianco).
function ImpegniDelGiorno({ girati, clientId }) {
  return (
    <span className="flex flex-col gap-1 mt-1" data-testid="girati-tooltip">
      {girati.map((g) => (
        <span key={g.girato_id} className="flex items-baseline gap-1.5">
          <span className="w-[5px] h-[5px] rounded-full bg-white/80 shrink-0 translate-y-[-1px]" />
          <span>
            <span className="font-semibold text-white">{g.client_id === clientId ? "Questo cliente" : g.client_name}</span>
            {g.assegnato_nome && ` · ${g.assegnato_nome}`}
            {g.genera_task && " · genera le task"}
          </span>
        </span>
      ))}
    </span>
  );
}

export function ModaleGirati({ client, onClose, onCambiato }) {
  const clientId = client?.client_id;
  const [vista, setVista] = useState(() => ({ y: new Date().getFullYear(), m: new Date().getMonth() }));
  const [girati, setGirati] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [caricamento, setCaricamento] = useState(false);
  const [giorno, setGiorno] = useState("");
  const [generaTask, setGeneraTask] = useState(true);
  // girato_id in modifica; null = nuovo girato.
  const [modifica, setModifica] = useState(null);
  const [confermaElimina, setConfermaElimina] = useState(null);
  const [saving, setSaving] = useState(false);

  // Griglia di 6 settimane da lunedì, come il DatePicker del design system.
  const celle = useMemo(() => {
    const primo = new Date(vista.y, vista.m, 1);
    const offset = (primo.getDay() + 6) % 7;
    return Array.from({ length: 42 }, (_, i) => new Date(vista.y, vista.m, 1 - offset + i));
  }, [vista]);

  const caricaCliente = useCallback(async () => {
    if (!clientId) return;
    const res = await apiFetch(`/clients/${clientId}/girati`);
    if (res.ok) {
      const dati = await res.json();
      setGirati(dati.items || []);
    } else {
      toast.error("Impossibile caricare i girati del cliente");
    }
  }, [clientId]);

  const caricaAgenda = useCallback(async () => {
    if (!clientId) return;
    const res = await apiFetch(`/girati?dal=${isoLocale(celle[0])}&al=${isoLocale(celle[41])}`);
    if (res.ok) setAgenda((await res.json()).items || []);
  }, [clientId, celle]);

  const nuovo = useCallback(() => {
    setModifica(null);
    setGiorno("");
    setGeneraTask(true);
    setConfermaElimina(null);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    nuovo();
    setVista({ y: new Date().getFullYear(), m: new Date().getMonth() });
    setCaricamento(true);
    caricaCliente().finally(() => setCaricamento(false));
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { caricaAgenda(); }, [caricaAgenda]);

  const perGiorno = useMemo(() => {
    const mappa = {};
    for (const g of agenda) (mappa[g.data] ||= []).push(g);
    return mappa;
  }, [agenda]);

  const delGiorno = useMemo(() => (giorno && perGiorno[giorno]) || [], [giorno, perGiorno]);

  // Il toggle delle task resta acceso finché non lo si spegne a mano: fissare
  // un girato serve quasi sempre a far partire le task.
  const scegliGiorno = (iso) => {
    setGiorno(iso);
    setConfermaElimina(null);
  };

  const apriModifica = (g) => {
    setModifica(g.girato_id);
    setGiorno(g.data);
    setGeneraTask(g.genera_task);
    setConfermaElimina(null);
    const d = daIso(g.data);
    setVista({ y: d.getFullYear(), m: d.getMonth() });
  };

  const spostaMese = (delta) => setVista(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const dopoSalvataggio = async (messaggio) => {
    toast.success(messaggio);
    nuovo();
    await Promise.all([caricaCliente(), caricaAgenda()]);
    onCambiato?.();
  };

  const salva = async () => {
    if (!giorno) return;
    setSaving(true);
    try {
      const res = await apiFetch(modifica ? `/girati/${modifica}` : `/clients/${clientId}/girati`, {
        method: modifica ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        // Senza assegnato_a: in modifica il backend conserva la persona già registrata.
        body: JSON.stringify({ data: giorno, genera_task: generaTask }),
      });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof dati.detail === "string" ? dati.detail : "Errore nel salvataggio del girato");
        return;
      }
      const task = dati.tasks_generated ? ` · ${dati.tasks_generated} task create` : "";
      await dopoSalvataggio(`${modifica ? "Girato aggiornato" : "Girato fissato"} il ${dataCorta(giorno)}${task}`);
    } catch {
      toast.error("Errore nel salvataggio del girato");
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (g) => {
    if (confermaElimina !== g.girato_id) {
      setConfermaElimina(g.girato_id);
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`/girati/${g.girato_id}`, { method: "DELETE" });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof dati.detail === "string" ? dati.detail : "Errore nell'eliminazione del girato");
        return;
      }
      const task = dati.tasks_deleted ? ` · ${dati.tasks_deleted} task aperte eliminate` : "";
      await dopoSalvataggio(`Girato del ${dataCorta(g.data)} eliminato${task}`);
    } catch {
      toast.error("Errore nell'eliminazione del girato");
    } finally {
      setSaving(false);
    }
  };

  const oggi = isoLocale(new Date());
  const stato = !giorno
    ? "Scegli un giorno nel calendario"
    : generaTask ? "Il girato genererà le task" : "Solo appuntamento, nessuna task";
  const prossimi = girati.filter((g) => g.data >= oggi).sort((a, b) => a.data.localeCompare(b.data));

  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      icon={Calendar}
      titolo="Girati"
      sottotitolo={client?.name}
      stato={stato}
      piedeSinistra={modifica && (
        <button
          type="button"
          onClick={nuovo}
          className="h-8 px-2.5 rounded-[9px] text-[12.5px] font-semibold text-[#565d6d] hover:bg-[#f3f3f8] transition-colors"
          data-testid="girati-annulla-modifica"
        >
          Annulla modifica
        </button>
      )}
      primario={{
        label: modifica ? "Salva modifiche" : "Fissa il girato",
        onClick: salva,
        disabled: !giorno,
        saving,
        testId: "girati-salva",
      }}
      testId="modale-girati"
    >
      {prossimi.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className={ETICHETTA}>Prossimi girati</span>
          <div className="flex flex-wrap gap-1.5" data-testid="girati-prossimi">
            {prossimi.map((g) => (
              <button
                key={g.girato_id}
                type="button"
                onClick={() => apriModifica(g)}
                className={`h-[30px] inline-flex items-center gap-1.5 pl-1 pr-2.5 rounded-full border text-[12.5px] font-semibold transition-colors ${
                  modifica === g.girato_id ? "border-[#5b5bd6] bg-[#f7f7fe] text-[#3f3fb0]" : "border-[#e4e4ed] bg-white text-[#3c414d] hover:border-[#c9c9f0]"
                }`}
                data-testid={`girato-chip-${g.girato_id}`}
              >
                {g.assegnato_nome && <UserAvatar name={g.assegnato_nome} picture={g.assegnato_picture} size={22} />}
                <span className={`scheda-font-mono ${g.assegnato_nome ? "" : "pl-1.5"}`}>{dataCorta(g.data)}</span>
                {g.genera_task && <ListChecks className="w-3.5 h-3.5 text-[#5b5bd6]" aria-label="genera le task" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendario: pallino accento = girati di questo cliente, grigio = altri clienti. */}
      <div className="flex flex-col gap-2 rounded-[14px] border border-[#e9e9f1] p-3" data-testid="girati-calendario">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => spostaMese(-1)} aria-label="Mese precedente" className="w-8 h-8 rounded-[9px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[14px] font-bold text-[#15161c] capitalize">
            {MESI_LUNGHI[vista.m]} <span className="scheda-font-mono">{vista.y}</span>
          </span>
          <button type="button" onClick={() => spostaMese(1)} aria-label="Mese successivo" className="w-8 h-8 rounded-[9px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {GIORNI_BREVI.map((g, i) => (
            <span key={i} className="h-6 flex items-center justify-center text-[11px] font-semibold uppercase text-[#9095a1]">{g}</span>
          ))}
          {celle.map((d) => {
            const iso = isoLocale(d);
            const fuori = d.getMonth() !== vista.m;
            const quelGiorno = perGiorno[iso] || [];
            const nostri = quelGiorno.filter((g) => g.client_id === clientId).length;
            const altri = quelGiorno.length - nostri;
            const scelto = iso === giorno;
            const cella = (
              <button
                key={iso}
                type="button"
                onClick={() => scegliGiorno(iso)}
                aria-pressed={scelto}
                aria-label={`${giornoEsteso(iso)}${quelGiorno.length ? `, ${quelGiorno.length} girati` : ""}`}
                className={`w-full h-11 rounded-[9px] flex flex-col items-center justify-center gap-[3px] scheda-font-mono text-[12.5px] transition-colors ${
                  scelto
                    ? "bg-[#5b5bd6] text-white font-semibold"
                    : quelGiorno.length
                      ? `bg-[#f4f4fb] hover:bg-[#eeeefc] ${fuori ? "text-[#b4b8c5]" : "text-[#15161c] font-semibold"}`
                      : iso === oggi
                        ? "text-[#5b5bd6] font-semibold ring-1 ring-inset ring-[#c9c9f0] hover:bg-[#eeeefc]"
                        : fuori ? "text-[#c3c6cf] hover:bg-[#f3f3f8]" : "text-[#3c414d] hover:bg-[#f3f3f8]"
                }`}
                data-testid={`girati-giorno-${iso}`}
              >
                {d.getDate()}
                <span className="flex gap-[3px] h-[5px]">
                  {Array.from({ length: Math.min(nostri, 3) }, (_, i) => (
                    <span key={`n${i}`} className={`w-[5px] h-[5px] rounded-full ${scelto ? "bg-white" : "bg-[#5b5bd6]"}`} />
                  ))}
                  {Array.from({ length: Math.min(altri, 3 - Math.min(nostri, 3)) }, (_, i) => (
                    <span key={`a${i}`} className={`w-[5px] h-[5px] rounded-full ${scelto ? "bg-white/60" : "bg-[#b4b8c5]"}`} />
                  ))}
                </span>
              </button>
            );
            if (!quelGiorno.length) return cella;
            // Al passaggio del mouse: chi è impegnato quel giorno e per quale cliente.
            return (
              <InfoTooltip
                key={iso}
                block
                title={giornoEsteso(iso).replace(/^./, (c) => c.toUpperCase())}
                description={<ImpegniDelGiorno girati={quelGiorno} clientId={clientId} />}
              >
                {cella}
              </InfoTooltip>
            );
          })}
        </div>
        <div className="flex items-center gap-3.5 px-1 text-[11.5px] text-[#9095a1]">
          <span className="inline-flex items-center gap-1.5"><span className="w-[6px] h-[6px] rounded-full bg-[#5b5bd6]" />Questo cliente</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-[6px] h-[6px] rounded-full bg-[#b4b8c5]" />Altri clienti</span>
          {caricamento && <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto" />}
        </div>
      </div>

      {giorno && (
        <div className="flex flex-col gap-3 modale-comparsa">
          <div className="flex flex-col gap-1.5">
            <span className={`${ETICHETTA} first-letter:uppercase`}>{giornoEsteso(giorno)}</span>
            {delGiorno.length === 0 ? (
              <span className="text-[12.5px] text-[#9095a1]" data-testid="girati-giorno-libero">Nessun girato fissato: giorno libero.</span>
            ) : (
              <div className="flex flex-col rounded-[12px] border border-[#eeeef4] divide-y divide-[#f0f0f6]" data-testid="girati-del-giorno">
                {delGiorno.map((g) => {
                  const nostro = g.client_id === clientId;
                  return (
                    <div key={g.girato_id} className={`flex items-center gap-2.5 px-3 py-2 ${modifica === g.girato_id ? "bg-[#f7f7fe]" : ""}`}>
                      {g.assegnato_nome && <UserAvatar name={g.assegnato_nome} picture={g.assegnato_picture} size={24} />}
                      <span className="flex-1 min-w-0 flex flex-col">
                        <span className="text-[13px] font-semibold text-[#15161c] truncate">{g.client_name}</span>
                        <span className="text-[12px] text-[#6b7280] truncate">
                          {[g.assegnato_nome, g.genera_task ? "genera le task" : "solo appuntamento"].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      {nostro && (
                        <>
                          <button
                            type="button"
                            onClick={() => apriModifica(g)}
                            className={`${SECONDARY_BTN} h-7 px-2.5 text-[12px]`}
                            data-testid={`girato-modifica-${g.girato_id}`}
                          >
                            Modifica
                          </button>
                          <button
                            type="button"
                            onClick={() => elimina(g)}
                            disabled={saving}
                            className={`h-7 px-2.5 rounded-[9px] text-[12px] font-semibold transition-colors ${
                              confermaElimina === g.girato_id ? "bg-[#c0392b] text-white" : "text-[#c0392b] hover:bg-[#fdecea]"
                            }`}
                            data-testid={`girato-elimina-${g.girato_id}`}
                          >
                            {confermaElimina === g.girato_id ? "Conferma" : "Elimina"}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {confermaElimina && (
              <span className="text-[12px] text-[#c0392b]">
                Eliminando il girato si eliminano anche le sue task non completate. Premi «Conferma» per procedere.
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setGeneraTask(!generaTask)}
            className="flex items-start gap-3 rounded-[12px] border border-[#e9e9f1] px-3 py-2.5 text-left hover:border-[#c9c9f0] transition-colors"
            data-testid="girati-genera-task"
          >
            <Interruttore checked={generaTask} label="Genera le task" />
            <span className="flex flex-col gap-0.5">
              <span className="text-[13px] font-semibold text-[#15161c]">Genera le task da questo girato</span>
              <span className="text-[12px] text-[#6b7280] leading-snug">
                {generaTask
                  ? "Le task del servizio (script, PED, editing…) si calcolano da questa data."
                  : "Solo un appuntamento in calendario: nessuna task."}
                {modifica && " Cambiando data o questa scelta, le task non completate del girato vengono rifatte."}
              </span>
            </span>
          </button>
        </div>
      )}
    </ModaleCliente>
  );
}

// ==================== 2. Call obiettivo ====================

export function ModaleCallObiettivo({ client, onClose, data, setData, saving, onSave, onRimuovi }) {
  const haData = !!soloData(client?.data_call_obiettivo);
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      icon={Phone}
      titolo="Call obiettivo"
      sottotitolo={client?.name}
      piedeSinistra={haData && (
        <button
          type="button"
          onClick={onRimuovi}
          disabled={saving}
          className="h-8 px-2.5 rounded-[9px] text-[12.5px] font-semibold text-[#c0392b] hover:bg-[#fdecea] transition-colors"
          data-testid="remove-call-obiettivo-btn"
        >
          Rimuovi data
        </button>
      )}
      primario={{ label: "Salva", onClick: onSave, disabled: !data, saving, testId: "save-call-obiettivo-btn" }}
      testId="modale-call-obiettivo"
    >
      <div className="flex flex-col gap-1.5">
        <span className={ETICHETTA}>Data della call</span>
        <DatePicker
          value={data}
          onChange={setData}
          className={`${CAMPO} scheda-font-mono text-left`}
          data-testid="call-obiettivo-date-input"
          data-autofocus=""
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {[7, 14, 30].map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setData(oggiPiu(g))}
            className="h-7 px-2.5 rounded-[8px] border border-[#e4e4ed] bg-white text-[12px] font-semibold text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors"
          >
            Tra {g} giorni
          </button>
        ))}
      </div>
      <span className="flex items-center gap-1.5 text-[12px] text-[#9095a1]">
        <Eye className="w-3.5 h-3.5" /> Visibile solo al PM assegnato e all&apos;admin
      </span>
    </ModaleCliente>
  );
}

// ==================== 3. Girato di recupero ====================

export function ModaleGiratoRecupero({ client, onClose, dati, setDati, saving, onSave }) {
  const attivo = !!dati.has_girato_recupero;
  const manca = attivo && !dati.data_girato_recupero;
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      tono="ambra"
      icon={RotateCcw}
      titolo="Girato di recupero"
      sottotitolo={client?.name}
      stato={manca ? "Indica la data del recupero" : attivo ? "Il recupero compare nella lista clienti" : "Nessun recupero questo mese"}
      primario={{ label: "Salva", onClick: onSave, disabled: manca, saving, testId: "save-recupero-btn" }}
      testId="modale-girato-recupero"
    >
      <button
        type="button"
        onClick={() => setDati({ ...dati, has_girato_recupero: !attivo })}
        className="w-full flex items-center gap-3 rounded-[14px] border p-3.5 text-left transition-colors duration-200"
        style={attivo ? { borderColor: "#fbd98e", background: "#fffaf0" } : { borderColor: "#e9e9f1", background: "#fff" }}
        data-autofocus=""
      >
        <span className="flex-1 flex flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-[#15161c]">Ha una giornata di recupero</span>
          <span className="text-[12.5px] text-[#6b7280]">Un secondo girato nel mese, oltre a quello pianificato</span>
        </span>
        <Interruttore checked={attivo} colore="#d97706" testId="has-recupero-checkbox" label="Ha una giornata di recupero" />
      </button>
      {attivo && (
        <div className="flex flex-col gap-1.5 modale-comparsa">
          <span className={ETICHETTA}>Data del recupero</span>
          <DatePicker
            value={soloData(dati.data_girato_recupero)}
            onChange={(v) => setDati({ ...dati, data_girato_recupero: v })}
            className={`${CAMPO} scheda-font-mono text-left`}
            data-testid="recupero-date-input"
          />
        </div>
      )}
    </ModaleCliente>
  );
}

// ==================== 4. Task del cliente ====================

export function ModaleTask({ client, onClose, tuttiTask, abilitati, setAbilitati, saving, onSave }) {
  const iniziali = useMemo(
    () => client?.enabled_tasks || tuttiTask.map((t) => t.id),
    [client, tuttiTask],
  );
  const modificato = JSON.stringify([...abilitati].sort()) !== JSON.stringify([...iniziali].sort());
  const nessuno = abilitati.length === 0;
  const tutti = abilitati.length === tuttiTask.length;
  const stato = nessuno
    ? { testo: "Seleziona almeno un task", colore: "#c0392b" }
    : modificato
      ? { testo: "I task verranno rigenerati", colore: "#b45309" }
      : { testo: "Nessuna modifica", colore: "#9095a1" };
  const toggle = (id) => setAbilitati(abilitati.includes(id) ? abilitati.filter((x) => x !== id) : [...abilitati, id]);
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      icon={ListChecks}
      titolo="Task del cliente"
      sottotitolo={client?.name}
      piedeSinistra={(
        <span className="flex items-center gap-1.5 text-[12px] font-medium" style={{ color: stato.colore }}>
          <RefreshCw className="w-3.5 h-3.5" /> {stato.testo}
        </span>
      )}
      primario={{ label: "Salva e rigenera", onClick: onSave, disabled: !modificato || nessuno, saving, testId: "save-tasks-btn" }}
      testId="modale-task"
    >
      <div className="flex items-center gap-2">
        <span className="text-[13.5px] font-semibold text-[#15161c]">Task generati ogni mese</span>
        <span className="h-[22px] px-2 rounded-full bg-[#eeeefc] text-[#5b5bd6] text-[11.5px] font-semibold scheda-font-mono inline-flex items-center">
          {abilitati.length} di {tuttiTask.length}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setAbilitati(tutti ? [] : tuttiTask.map((t) => t.id))}
          className="text-[12.5px] font-semibold text-[#5b5bd6] hover:underline"
        >
          {tutti ? "Deseleziona tutti" : "Seleziona tutti"}
        </button>
      </div>
      <div className="rounded-[14px] border border-[#e9e9f1] overflow-hidden">
        {tuttiTask.map((t, i) => {
          const on = abilitati.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(t.id)}
              className={`w-full h-11 flex items-center gap-3 px-3.5 text-left hover:bg-[#fbfbfd] transition-colors ${i ? "border-t border-[#f0f0f6]" : ""}`}
              data-testid={`task-toggle-${t.id}`}
              {...(i === 0 ? { "data-autofocus": "" } : {})}
            >
              <span
                className="w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-colors duration-150"
                style={on ? { background: "#5b5bd6", borderColor: "#5b5bd6" } : { borderColor: "#c9ccd6", background: "#fff" }}
              >
                {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </span>
              <span className="text-[13.5px]" style={{ color: on ? "#15161c" : "#9095a1" }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </ModaleCliente>
  );
}

// ==================== 5. Follower mensili ====================

const PIATTAFORME = [
  { id: "instagram", label: "Instagram", colore: "#e1306c" },
  { id: "tiktok", label: "TikTok", colore: "#15161c" },
  { id: "youtube", label: "YouTube", colore: "#ff0000" },
  { id: "facebook", label: "Facebook", colore: "#1877f2" },
];

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const meseBreve = (ym) => (ym ? `${MESI[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}` : "");
const mesePrima = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// `storico`: le righe di /follower-stats/{client_id}. Il mese precedente dà la
// base della crescita; se il mese scelto è già stato salvato, i campi partono
// dai suoi valori (salvare di nuovo aggiorna la stessa riga).
export function ModaleFollower({ client, onClose, dati, setDati, storico, saving, onSave }) {
  const ym = dati.year_month;
  const prec = (storico || []).find((s) => s.year_month === (ym ? mesePrima(ym) : ""));

  useEffect(() => {
    if (!client || !ym) return;
    const corrente = (storico || []).find((s) => s.year_month === ym);
    setDati((d) => ({
      ...d,
      ...Object.fromEntries(PIATTAFORME.map((p) => [p.id, corrente?.[p.id] != null ? String(corrente[p.id]) : ""])),
    }));
  }, [client, ym, storico]); // eslint-disable-line react-hooks/exhaustive-deps

  const compilate = PIATTAFORME.filter((p) => dati[p.id] !== "" && dati[p.id] != null).length;
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      tono="lilla"
      icon={TrendingUp}
      titolo="Follower mensili"
      sottotitolo={client?.name}
      headerExtra={(
        <MonthPicker
          value={ym}
          onChange={(v) => setDati((d) => ({ ...d, year_month: v }))}
          className="h-[34px] w-[118px] rounded-[9px] border border-[#e4e4ed] bg-white px-2.5 text-[12.5px] scheda-font-mono shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] hover:border-[#c9c9f0] mt-0.5"
          data-testid="follower-month-input"
        />
      )}
      stato={`${compilate} di ${PIATTAFORME.length} compilate`}
      primario={{ label: "Salva follower", onClick: onSave, disabled: compilate === 0, saving, testId: "save-follower-btn" }}
      testId="modale-follower"
    >
      <span className="text-[12.5px] text-[#565d6d]">Follower a fine mese. La crescita si calcola rispetto al mese precedente.</span>
      <div className="flex flex-col">
        <div className="grid grid-cols-[1fr_92px_130px_76px] gap-3 pb-2 text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]">
          <span>Piattaforma</span>
          <span className="text-right">{ym ? meseBreve(mesePrima(ym)) : ""}</span>
          <span className="text-right">{meseBreve(ym)}</span>
          <span className="text-right">Crescita</span>
        </div>
        {PIATTAFORME.map((p, i) => {
          const base = prec?.[p.id];
          const val = dati[p.id];
          const num = val === "" || val == null ? null : Number(val);
          const crescita = num != null && base ? ((num - base) / base) * 100 : null;
          return (
            <div key={p.id} className="grid grid-cols-[1fr_92px_130px_76px] gap-3 items-center py-2 border-t border-[#f0f0f6]">
              <span className="flex items-center gap-2 text-[13.5px] font-medium text-[#15161c]">
                <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: p.colore }} />
                {p.label}
              </span>
              <span className="text-right scheda-font-mono text-[13px] text-[#9095a1]">{base != null ? formatNumber(base) : "—"}</span>
              <input
                type="text"
                inputMode="numeric"
                value={val != null && val !== "" ? formatNumber(val) : ""}
                onChange={(e) => {
                  const pulito = e.target.value.replace(/[^\d]/g, "");
                  setDati((d) => ({ ...d, [p.id]: pulito }));
                }}
                placeholder="—"
                className={`${CAMPO} h-9 text-right scheda-font-mono`}
                data-testid={`${p.id}-input`}
                {...(i === 0 ? { "data-autofocus": "" } : {})}
              />
              <span
                className="text-right scheda-font-mono text-[13px] font-semibold"
                style={{ color: crescita == null ? "#b4b8c5" : crescita >= 0 ? "#0f7040" : "#c0392b" }}
              >
                {crescita == null ? "—" : `${crescita >= 0 ? "+" : ""}${formatNumber(crescita, { maximumFractionDigits: 1 })}%`}
              </span>
            </div>
          );
        })}
      </div>
    </ModaleCliente>
  );
}

// ==================== 6. Servizi e importi ====================

const REPARTO_STILE = {
  tiktok_shop: { sigla: "TS", tile: { bg: "#fdeaf3", color: "#b83280" } },
  gestione_meta_tiktok: { sigla: "M+T", tile: { bg: "#e6efff", color: "#2f5fd0" } },
};
const SOCIAL_8 = "gmt_social_organico_8";
const SOCIAL_12 = "gmt_social_organico_12";

// I singoli servizi di ogni reparto restano selezionabili come chip: il
// prototipo li ometteva, ma `ts_tiktok_shop` è ciò che attiva la fee TikTok nei
// compensi, e gli altri alimentano i badge della scheda. Social organico 8/12
// diventa un segmentato perché è una scelta esclusiva.
export function ModaleServizi({ client, onClose, reparti, serviziPerReparto, dati, onToggleReparto, onToggleServizio, onImporto, totale, saving, onSave }) {
  const fmt = (v) => formatCurrency(v, { decimals: 0 });
  // Confronto con quanto salvato sul cliente, normalizzato come lo inizializza
  // handleEditServizi: il salvataggio si abilita solo se qualcosa cambia.
  const normalizza = (per) => JSON.stringify(reparti.map((r) => {
    const d = per(r.id);
    return [r.id, !!d.enabled, [...(d.servizi || [])].sort(), Number(d.importo) || 0];
  }));
  const modificato = !!client && normalizza((id) => dati[id] || {}) !== normalizza((id) => ({
    enabled: client.reparti?.includes(id),
    servizi: client.servizi_reparto?.[id]?.servizi,
    importo: client.servizi_reparto?.[id]?.importo,
  }));
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      icon={Package}
      titolo="Servizi e importi"
      sottotitolo={client?.name}
      piedeSinistra={(
        <span className="flex flex-col">
          <span className="text-[11.5px] text-[#9095a1]">Totale mensile</span>
          <span className="scheda-font-mono text-[18px] font-semibold transition-colors" style={{ color: totale > 0 ? "#0d8377" : "#9095a1" }}>
            {fmt(totale)}
          </span>
        </span>
      )}
      primario={{ label: "Salva servizi", onClick: onSave, disabled: !modificato, saving, testId: "save-servizi-btn" }}
      testId="modale-servizi"
    >
      <span className="text-[12.5px] text-[#565d6d]">Seleziona i reparti e i servizi attivi per questo cliente, con i relativi importi mensili.</span>
      <div className="flex flex-col gap-2.5 max-h-[52vh] overflow-y-auto -mx-1 px-1 py-0.5">
        {reparti.map((rep, i) => {
          const d = dati[rep.id] || { servizi: [], importo: 0, enabled: false };
          const st = REPARTO_STILE[rep.id] || { sigla: rep.label.slice(0, 2).toUpperCase(), tile: ICON_TILES.teal };
          const servizi = serviziPerReparto[rep.id] || [];
          const haSocial = servizi.some((s) => s.id === SOCIAL_8);
          const altri = servizi.filter((s) => s.id !== SOCIAL_8 && s.id !== SOCIAL_12);
          const social = d.servizi.includes(SOCIAL_12) ? SOCIAL_12 : d.servizi.includes(SOCIAL_8) ? SOCIAL_8 : null;
          return (
            <div
              key={rep.id}
              className="rounded-[14px] border transition-[border-color,background-color,box-shadow] duration-200"
              style={d.enabled
                ? { borderColor: "#b7e3dc", background: "#fbfefd", boxShadow: "0 6px 16px -10px rgba(13,131,119,.4)" }
                : { borderColor: "#e9e9f1", background: "#fff" }}
            >
              <button
                type="button"
                onClick={() => onToggleReparto(rep.id)}
                className="w-full flex items-center gap-3 p-3.5 text-left"
                {...(i === 0 ? { "data-autofocus": "" } : {})}
              >
                <span
                  className="w-9 h-9 rounded-[11px] flex items-center justify-center text-[11px] font-bold shrink-0 transition-colors"
                  style={d.enabled ? { background: st.tile.bg, color: st.tile.color } : { background: "#f3f3f8", color: "#b4b8c5" }}
                >
                  {st.sigla}
                </span>
                <span className="flex-1 text-[14px] font-semibold text-[#15161c]">{rep.label}</span>
                {d.enabled && d.importo > 0 && (
                  <span className="scheda-font-mono text-[13.5px] font-semibold text-[#0d8377]">{fmt(d.importo)}</span>
                )}
                <Interruttore checked={d.enabled} colore="#0d8377" testId={`reparto-${rep.id}-checkbox`} label={rep.label} />
              </button>
              {d.enabled && (
                <div className="px-3.5 pb-3.5 pl-[62px] flex flex-col gap-3 modale-comparsa">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="relative block w-[150px]">
                      <span className="absolute left-[11px] top-1/2 -translate-y-1/2 text-[13px] text-[#b4b8c5] pointer-events-none">€</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={d.importo ? formatNumber(d.importo) : ""}
                        onChange={(e) => onImporto(rep.id, e.target.value.replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, ""))}
                        placeholder="0"
                        className={`${CAMPO} h-[38px] pl-6 pr-12 text-right scheda-font-mono [--mc:#0d8377] [--mc-anello:rgba(13,131,119,.14)]`}
                        data-testid={`importo-${rep.id}`}
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11.5px] text-[#9095a1] pointer-events-none">/mese</span>
                    </span>
                    {haSocial && (
                      <div className="inline-flex p-[3px] rounded-[10px] bg-[#f3f3f8] gap-0.5" role="radiogroup" aria-label="Social organico">
                        {[[SOCIAL_8, "8 video"], [SOCIAL_12, "12 video"]].map(([id, label]) => {
                          const sel = social === id;
                          return (
                            <button
                              key={id}
                              type="button"
                              role="radio"
                              aria-checked={sel}
                              onClick={() => onToggleServizio(rep.id, id)}
                              className={`h-7 px-2.5 rounded-[8px] text-[12px] font-semibold transition-colors ${sel ? "bg-white text-[#15161c]" : "text-[#6b7280] hover:text-[#15161c]"}`}
                              style={sel ? { boxShadow: SHADOWS.appoggiato } : undefined}
                              data-testid={`servizio-${id}-checkbox`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {altri.map((s) => (
                      <ToggleChip
                        key={s.id}
                        checked={d.servizi.includes(s.id)}
                        onChange={() => onToggleServizio(rep.id, s.id)}
                        testId={`servizio-${s.id}-checkbox`}
                      >
                        {s.label}
                      </ToggleChip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModaleCliente>
  );
}

// ==================== 7. Dati amministrativi ====================

export function ModaleDatiAmministrativi({ client, onClose, dati, setDati, puoRinominare, saving, onSave }) {
  // Con almeno un pacchetto "pagato" si deriva dai pagamenti registrati nella
  // scheda cliente (backend/stato_pagamenti.py): qui è solo un'etichetta.
  const gestito = !!client?.stato_pagamenti?.gestito;
  const meseCorrente = client?.stato_pagamenti?.mese_corrente;
  const arretrati = client?.stato_pagamenti?.arretrati || [];
  const iniziale = useMemo(() => (client ? {
    name: client.name || "",
    importo_mensile: client.importo_mensile ?? "",
    contratto_url: client.contratto_url || "",
    ha_pagato: client.ha_pagato !== false,
  } : null), [client]);
  const modificato = !!iniziale && (
    (dati.name || "") !== iniziale.name
    || String(dati.importo_mensile ?? "") !== String(iniziale.importo_mensile ?? "")
    || (dati.contratto_url || "") !== iniziale.contratto_url
    || (!gestito && dati.ha_pagato !== iniziale.ha_pagato)
  );
  const linkValido = /^https?:\/\/\S+$/i.test(dati.contratto_url || "");
  const pagato = !!dati.ha_pagato;
  return (
    <ModaleCliente
      open={!!client}
      onClose={onClose}
      icon={FileText}
      tono="accento"
      titolo="Dati amministrativi"
      sottotitolo={(puoRinominare && (dati.name || "").trim()) || client?.name}
      stato={modificato ? "Modifiche da salvare" : "Nessuna modifica"}
      primario={{ label: "Salva", onClick: onSave, disabled: !modificato, saving, testId: "save-admin-btn" }}
      testId="modale-dati-amministrativi"
    >
      {puoRinominare && (
        <div className="flex flex-col gap-1.5">
          <span className="flex items-center gap-2">
            <span className={ETICHETTA}>Nome cliente</span>
            <span className="h-5 px-1.5 rounded-full text-[11px] font-semibold inline-flex items-center" style={{ background: "#ececf3", color: "#3c414d" }}>Admin / AMM</span>
          </span>
          <input
            type="text"
            value={dati.name}
            onChange={(e) => setDati({ ...dati, name: e.target.value })}
            className={CAMPO}
            data-testid="admin-name-input"
            data-autofocus=""
          />
          <span className="text-[12px] text-[#9095a1]">Il nome viene aggiornato ovunque venga usato.</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-3">
        <div className="flex flex-col gap-1.5">
          <span className={ETICHETTA}>Importo mensile</span>
          <span className="relative block">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#b4b8c5] pointer-events-none">€</span>
            <input
              type="text"
              inputMode="decimal"
              value={dati.importo_mensile !== "" && dati.importo_mensile != null ? formatNumber(dati.importo_mensile) : ""}
              onChange={(e) => setDati({ ...dati, importo_mensile: e.target.value.replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, "") })}
              placeholder="0"
              className={`${CAMPO} pl-7 text-right scheda-font-mono`}
              data-testid="admin-importo-input"
              {...(!puoRinominare ? { "data-autofocus": "" } : {})}
            />
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={ETICHETTA}>Link contratto</span>
          <span className="relative flex items-center gap-2">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b4b8c5] pointer-events-none" />
            <input
              type="text"
              value={dati.contratto_url}
              onChange={(e) => setDati({ ...dati, contratto_url: e.target.value })}
              placeholder="https://drive.google.com/…"
              className={`${CAMPO} pl-9`}
              data-testid="admin-contratto-input"
            />
            {linkValido && (
              <a
                href={dati.contratto_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-[42px] h-[42px] rounded-[10px] border border-[#e4e4ed] flex items-center justify-center text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors shrink-0"
                aria-label="Apri il contratto"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </span>
        </div>
      </div>
      {gestito ? (
        <div
          className="w-full flex items-center gap-3 rounded-[14px] border p-3.5"
          style={pagato ? { borderColor: "#bfe8d2", background: "#f4fbf7" } : { borderColor: "#f6c9c3", background: "#fef6f5" }}
          data-testid="admin-pagato-etichetta"
        >
          <span
            className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0"
            style={pagato ? { background: "#e7f7ee", color: "#0f7040" } : { background: "#fdecea", color: "#c0392b" }}
          >
            <CreditCard className="w-[18px] h-[18px]" strokeWidth={1.9} />
          </span>
          <span className="flex-1 flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-[14px] font-semibold text-[#15161c]">
              {pagato ? "Pagato questo mese" : "Non pagato questo mese"}
              {meseCorrente?.dovute > 0 && (
                <span className="scheda-font-mono text-[11.5px] font-semibold" style={{ color: pagato ? "#0f7040" : "#c0392b" }}>
                  {meseCorrente.pagate}/{meseCorrente.dovute}
                </span>
              )}
            </span>
            <span className="text-[12.5px] text-[#6b7280]">Si aggiorna quando l&apos;amministrazione registra i pagamenti nella scheda cliente.</span>
            {arretrati.length > 0 && (
              <span className="text-[12.5px] font-semibold text-[#c0392b]">
                Arretrati: {arretrati.map((a) => a.nome_mese).join(", ")}
              </span>
            )}
          </span>
        </div>
      ) : (
      <button
        type="button"
        onClick={() => setDati({ ...dati, ha_pagato: !pagato })}
        className="w-full flex items-center gap-3 rounded-[14px] border p-3.5 text-left transition-colors duration-200"
        style={pagato ? { borderColor: "#bfe8d2", background: "#f4fbf7" } : { borderColor: "#f6c9c3", background: "#fef6f5" }}
      >
        <span
          className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0 transition-colors"
          style={pagato ? { background: "#e7f7ee", color: "#0f7040" } : { background: "#fdecea", color: "#c0392b" }}
        >
          <CreditCard className="w-[18px] h-[18px]" strokeWidth={1.9} />
        </span>
        <span className="flex-1 flex flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-[#15161c]">Ha pagato questo mese</span>
          <span className="text-[12.5px] font-medium" style={{ color: pagato ? "#0f7040" : "#c0392b" }}>{pagato ? "Pagato" : "Non pagato"}</span>
        </span>
        <Interruttore checked={pagato} colore="#10b981" testId="admin-pagato-checkbox" label="Ha pagato questo mese" />
      </button>
      )}
    </ModaleCliente>
  );
}
